import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, CalendarIcon } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { it as itLocale, enUS } from "date-fns/locale";

/**
 * Generic admin promo manager.
 * Pass `promoType` ('generic' or 'hotel') to scope listing/creation.
 */
export default function PromoManager({ promoType = "generic", title, tag, addLabel, showHotelField = false }) {
  const { t, i18n } = useTranslation();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    code: "", hotel_name: "", discount_type: "percent", discount_value: "",
    max_uses: "", valid_until: null, active: true, description: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const [reloadKey, setReloadKey] = useState(0);
  const reload = () => setReloadKey((k) => k + 1);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/promo-codes", { params: { promo_type: promoType } });
        if (!cancelled) setList(data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e.response?.data?.detail) || e.message);
      }
      finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [promoType, reloadKey]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        code: form.code.toUpperCase().trim(),
        promo_type: promoType,
        discount_type: form.discount_type,
        discount_value: parseFloat(form.discount_value),
        active: form.active,
        description: form.description || null,
      };
      if (showHotelField) payload.hotel_name = form.hotel_name || null;
      if (form.max_uses) payload.max_uses = parseInt(form.max_uses, 10);
      if (form.valid_until) payload.valid_until = form.valid_until.toISOString();
      await api.post("/admin/promo-codes", payload);
      toast.success(i18n.language === "en" ? "Code created" : "Codice creato");
      setForm({ code: "", hotel_name: "", discount_type: "percent", discount_value: "", max_uses: "", valid_until: null, active: true, description: "" });
      setOpen(false);
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  const toggleActive = async (id, active) => {
    try {
      await api.patch(`/admin/promo-codes/${id}`, { active });
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const remove = async (id) => {
    if (!window.confirm(i18n.language === "en" ? "Delete this code?" : "Eliminare il codice?")) return;
    try {
      await api.delete(`/admin/promo-codes/${id}`);
      toast.success(i18n.language === "en" ? "Deleted" : "Eliminato");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#FFD700] mb-2">{tag}</p>
          <h1 className="font-display text-4xl font-bold tracking-tight">{title}</h1>
          <p className="text-zinc-400 mt-1 text-sm">{list.length} {i18n.language === "en" ? "codes" : "codici"}</p>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold" data-testid="add-promo-button">
              <Plus className="w-4 h-4 mr-2" /> {addLabel}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display">{addLabel}</DialogTitle>
            </DialogHeader>
            <form onSubmit={create} className="space-y-3">
              <div>
                <Label>{i18n.language === "en" ? "Code" : "Codice"}</Label>
                <Input required value={form.code} onChange={(e) => setField("code", e.target.value)}
                       className="bg-zinc-950 border-zinc-800 mt-1.5 uppercase font-mono"
                       placeholder="ELBASUMMER25" data-testid="promo-code-input" />
              </div>
              {showHotelField && (
                <div>
                  <Label>{i18n.language === "en" ? "Hotel name" : "Nome hotel"}</Label>
                  <Input value={form.hotel_name} onChange={(e) => setField("hotel_name", e.target.value)}
                         className="bg-zinc-950 border-zinc-800 mt-1.5"
                         placeholder="Hotel Bella Vista" data-testid="promo-hotel-input" />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{i18n.language === "en" ? "Type" : "Tipo sconto"}</Label>
                  <Select value={form.discount_type} onValueChange={(v) => setField("discount_type", v)}>
                    <SelectTrigger className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="promo-discount-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                      <SelectItem value="percent">%</SelectItem>
                      <SelectItem value="fixed">€ fisso (totale)</SelectItem>
                      <SelectItem value="per_day">€ / giorno</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{i18n.language === "en" ? "Value" : "Valore"}</Label>
                  <Input required type="number" step="0.01" min="0.01" value={form.discount_value}
                         onChange={(e) => setField("discount_value", e.target.value)}
                         className="bg-zinc-950 border-zinc-800 mt-1.5"
                         data-testid="promo-discount-value" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{i18n.language === "en" ? "Max uses" : "Usi max"}</Label>
                  <Input type="number" min="1" value={form.max_uses}
                         onChange={(e) => setField("max_uses", e.target.value)}
                         className="bg-zinc-950 border-zinc-800 mt-1.5"
                         placeholder="∞" data-testid="promo-max-uses" />
                </div>
                <div>
                  <Label>{i18n.language === "en" ? "Valid until" : "Valido fino al"}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button type="button" variant="outline"
                              className="bg-zinc-950 border-zinc-800 hover:bg-zinc-900 hover:text-zinc-100 mt-1.5 w-full justify-start font-normal"
                              data-testid="promo-valid-until-trigger">
                        <CalendarIcon className="w-4 h-4 mr-2 text-[#FFD700]" />
                        {form.valid_until
                          ? format(form.valid_until, "PPP", { locale: i18n.language === "en" ? enUS : itLocale })
                          : (i18n.language === "en" ? "Pick a date" : "Scegli una data")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-800" align="start" data-testid="promo-valid-until-popover">
                      <Calendar mode="single" selected={form.valid_until}
                                onSelect={(d) => setField("valid_until", d || null)}
                                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                                locale={i18n.language === "en" ? enUS : itLocale}
                                initialFocus
                                className="text-zinc-100" />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <div>
                <Label>{i18n.language === "en" ? "Description" : "Descrizione"}</Label>
                <Input value={form.description} onChange={(e) => setField("description", e.target.value)}
                       className="bg-zinc-950 border-zinc-800 mt-1.5"
                       data-testid="promo-description-input" />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={submitting} className="bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold w-full" data-testid="promo-submit-button">
                  {submitting ? "…" : t("common.save")}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-zinc-800 rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-800 hover:bg-transparent">
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">{i18n.language === "en" ? "Code" : "Codice"}</TableHead>
              {showHotelField && <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">{i18n.language === "en" ? "Hotel" : "Hotel"}</TableHead>}
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">{i18n.language === "en" ? "Discount" : "Sconto"}</TableHead>
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">{i18n.language === "en" ? "Uses" : "Utilizzi"}</TableHead>
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">{i18n.language === "en" ? "Valid until" : "Scade"}</TableHead>
              <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">{i18n.language === "en" ? "Active" : "Attivo"}</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-zinc-500 text-center py-8">{t("common.loading")}</TableCell></TableRow>
            ) : list.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-zinc-500 text-center py-8">{i18n.language === "en" ? "No codes yet" : "Nessun codice"}</TableCell></TableRow>
            ) : list.map((p) => (
              <TableRow key={p.id} className="border-zinc-800" data-testid="promo-row">
                <TableCell className="font-mono uppercase font-semibold text-[#FFD700]">{p.code}</TableCell>
                {showHotelField && <TableCell className="text-sm">{p.hotel_name || "—"}</TableCell>}
                <TableCell>{p.discount_type === "percent" ? `${p.discount_value}%` : p.discount_type === "per_day" ? `€${p.discount_value.toFixed(2)}/g` : `€${p.discount_value.toFixed(2)}`}</TableCell>
                <TableCell className="text-sm">{p.used_count}{p.max_uses ? `/${p.max_uses}` : ""}</TableCell>
                <TableCell className="text-sm text-zinc-400">
                  {p.valid_until ? new Date(p.valid_until).toLocaleDateString() : "—"}
                </TableCell>
                <TableCell>
                  <Switch checked={p.active} onCheckedChange={(v) => toggleActive(p.id, v)} data-testid="promo-active-switch" />
                </TableCell>
                <TableCell>
                  <Button variant="ghost" size="sm" onClick={() => remove(p.id)} className="text-red-400 hover:text-red-300 hover:bg-red-950/30" data-testid="promo-delete-button">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
